import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { AlertCircle } from 'lucide-react';
import ShippingLegForm from '../legs/ShippingLegForm';
import ShippingFlowFooter from '../../components/ShippingFlowFooter'; // Import the new footer component
import { formatPrice } from '../../utils/quantities';
import { useTheme } from '../../context/ThemeContext';
import ResponsiveTabs from '../../components/ResponsiveTabs';
import { useScrollTo } from '../../hooks/useScrollTo';
import { useFormStore } from '../../stores/formData';

const ShippingFlowLegs = ({
  onClickContinue,
  canShowBack,
  onClickBack,
  originRef,
  destinationRef
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentLeg, setCurrentLeg] = useState(0);
  const { scrollToTop } = useScrollTo();
  const [waitingForNewLeg, setWaitingForNewLeg] = useState(null);

  const legs = useFormStore((state) => state.formData.legs);
  const tabs = useFormStore((state) => state.formData.tabs);
  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers); 
  const formData = useFormStore((state) => state.formData);

  useEffect(() => {
    actions.form.setCurrentPage(0, 'legs');
  }, []);

  useEffect(() => {
    if (waitingForNewLeg !== null &&
        legs.length > waitingForNewLeg) {
      setCurrentLeg(legs.length - 1);
      setWaitingForNewLeg(null);
      scrollToTop();
    }
  }, [legs, waitingForNewLeg, scrollToTop]);

  useEffect(() => {
    if (formData.activeLegIndex != null &&
      formData.activeLegIndex !== currentLeg) {
      const newIndex = formData.activeLegIndex;
      const fieldType = formData.activeFieldType;
      
      actions.form.updateField('activeLegIndex', null);
      actions.form.updateField('activeFieldType', null);
      
      setTimeout(() => {
        setCurrentLeg(newIndex);
        
        if (fieldType) {
          // Scroll to section
          setTimeout(() => {
            const targetRef = fieldType === 'destination' ? destinationRef : originRef;
            if (targetRef?.current) {
              targetRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            }
            
            // Focus the input after scroll completes
            setTimeout(() => {
              const inputId = fieldType === 'destination' 
                ? `leg${newIndex}-to-address` 
                : `leg${newIndex}-from-address`;
              const input = document.getElementById(inputId);
              if (input) {
                input.focus();
              }
            }, 300); // Wait for smooth scroll to finish
          }, 150);
        } else {
          scrollToTop();
        }
      }, 100);
    }
  }, [
    formData.activeLegIndex,
    formData.activeFieldType,
    actions,
    currentLeg,
    originRef,
    destinationRef,
    scrollToTop
  ]);

  const addLeg = useCallback(() => {
    helpers.addLeg();
    setWaitingForNewLeg(legs.length);
  }, [legs, helpers]);

  const removeLeg = useCallback((legIndex) => {
    actions.form.removeLeg(legIndex);
  }, [actions]);

  const orderTotal = useMemo(() => {
    return helpers.getOrderTotal();
  }, [legs, helpers]);

  const handleTabChange = useCallback((newIndex) => {
    setCurrentLeg(newIndex);
  }, []);
  
  const handleTabClose = useCallback((index) => {
    const currentLegs = legs || [];
    if (currentLegs.length <= 1) {
      // Do not allow removing the last leg
      return;
    }
    actions.form.removeLeg(index);
    // If the closed tab was the current page, move to previous or next tab
    if (currentLeg === index) {
      if (index === currentLegs.length - 1) {
        // Closed the last tab, move to previous
        setCurrentLeg(index - 1);
      } else {
        // Stay on the same index, which is now the next tab
        setCurrentLeg(index);
      }
    } else if (currentLeg > index) {
      // If current page was after the closed tab, shift back by one
      setCurrentLeg(currentLeg - 1);
    }
  }, [legs, actions, currentLeg]);

  const handleAddTab = useCallback(() => {
    helpers.addLeg();
    setWaitingForNewLeg(legs.length);
  }, [legs, actions]);
  
  const tabsWithTotal = useMemo(() => {
    return tabs?.map((tab, index) => {
      const legTotal = helpers.getLegTotal(index);
      const leg = helpers.getLeg(index);
      const subtitle = `${leg?.fromAddress?.cruisePort?.name || 
      leg?.fromAddress?.city || 'Origin'} → ${leg?.toAddress?.cruisePort?.name || 
      leg?.toAddress?.city || 'Destination'}`;
      return {
        ...tab,
        price: `${formatPrice(legTotal)}`,
        subtitle,
        closable: tabs?.length > 1 // Only allow closing if more than one leg
      };
    });
  }, [legs, tabs, helpers]);

  const containerClassName = clsx(
    'md:p-4 -scroll-mb-10',
    {
      'bg-slate-900': isDark,
      'bg-white': !isDark
    }
  );

  const summaryClassName = clsx(
    'mt-4',
    {
      'bg-slate-800': isDark,
      'bg-white': !isDark
    }
  );

  const totalPriceLabelClassName = clsx(
    'text-xl',
    {
      'text-slate-100': isDark,
      'text-slate-900': !isDark
    }
  );

  const totalPriceClassName = clsx(
    'text-xl font-bold',
    {
      'text-slate-100': isDark,
      'text-slate-900': !isDark
    }
  );

  const missing = helpers.checkLegsComplete();
  const canContinueBooking = Object.keys(missing).length === 0;
  
  const formattedMissing = useMemo(() => {
    try {
      // Ensure missing is an object
      if (!missing || typeof missing !== 'object') {
        return [];
      }
      
      const keys = Object.keys(missing);
      
      // Filter keys for current leg
      const filteredKeys = keys.filter((key) => {
        return typeof key === 'string' && key.startsWith(`leg${currentLeg}`);
      });
      
      // Remove leg prefix from each key and return objects with testId and label
      return filteredKeys.map((key) => {
        const fieldPrefix = `leg${currentLeg}-`;
        const testId = key.replace(fieldPrefix, '');

        // Format field name for display
        const label = testId
          .replace(/[-_]/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        // Check if element exists in DOM
        const element = document.querySelector(`[data-testid="${fieldPrefix}${testId}"]`);
        const clickable = !!element;
        
        return {
          testId: `${fieldPrefix}${testId}`,
          label,
          originalKey: key,
          clickable
        };
      });
    } catch (error) {
      console.error('Error formatting missing fields:', error);
      return [];
    }
  }, [missing, currentLeg]);

  const scrollToField = useCallback((testId) => {
    try {
      const element = document.querySelector(`[data-testid="${testId}"]`);
      if (element) {
        // Scroll to element with smooth behavior
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add highlight class
        element.classList.add('border-red-500', 'ring-2', 'ring-red-500');
        
        // Focus if it's an input
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.focus();
        }
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          element.classList.remove('border-red-500', 'ring-2', 'ring-red-500');
        }, 3000);
      } else {
        console.warn(`Element with data-testid="${testId}" not found`);
      }
    } catch (error) {
      console.error('Error scrolling to field:', error);
    }
  }, []);

  return (
    <div className={containerClassName}>
      <div className="w-full lg:w-[1136px] mx-auto">
        {/* Main Content */}
        <div data-testid="tab-container" className="space-y-2">
          <ResponsiveTabs
            tabs={tabsWithTotal}
            activeTab={currentLeg}
            onTabChange={handleTabChange}
            onTabClose={handleTabClose}
            onAddTab={handleAddTab}
            maxTabs={2} // Limit to 2 legs
            canAddMoreTabs={canContinueBooking && legs.length < 2} 
          >
            {helpers.getLegs().map((leg) => (
              <div key={leg.id} className={clsx(
                'px-0 md:p-2',
                { 'hidden': leg.legIndex !== currentLeg }
              )}>
                <ShippingLegForm
                  legIndex={leg.legIndex}
                  onRemoveLeg={() => removeLeg(leg.legIndex)}
                  originRef={originRef}
                  destinationRef={destinationRef}
                />
              </div>
            ))}
          </ResponsiveTabs>
        </div>
        
        {/* Summary */}
        <div className={summaryClassName}>
          <div className="flex items-center justify-end mr-2">
            <div className="text-right">
              <div>
                <span className={totalPriceLabelClassName}>Trip Total: </span>
                <span className={totalPriceClassName}>{formatPrice(orderTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {formattedMissing.length > 0 && formattedMissing.length <= 2 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="text-red-500 mt-0.5 flex-shrink-0" size={18} />
            <div className="text-sm text-red-700">
              <span className="font-medium">Missing required fields: </span>
              {formattedMissing.map((field, index) => (
                <span key={field.testId}>
                  {field.clickable ? (
                    <button
                      type="button"
                      onClick={() => scrollToField(field.testId)}
                      className="text-red-600 underline hover:text-red-800 font-medium cursor-pointer"
                    >
                      {field.label}
                    </button>
                  ) : (
                    <span className="text-red-700 font-medium">
                      {field.label}
                    </span>
                  )}
                  {index < formattedMissing.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer with buttons */}
        <ShippingFlowFooter
          onBack={() => onClickBack?.()}
          onSecondaryAction={addLeg}
          onPrimaryAction={() => onClickContinue?.()}
          showBack={canShowBack?.()}
          showSecondaryButton={legs.length < 2} // Show "Add Leg" if less than 5 legs 
          disabled={!canContinueBooking}
          secondaryButtonText="Add another Leg"
          primaryButtonText="Continue"
          primaryHelpText="You'll provide trip details on the next step"
        />
      </div>
    </div>
  );
};

ShippingFlowLegs.propTypes = {
  onClickContinue: PropTypes.func.isRequired,
  canShowBack: PropTypes.func,
  onClickBack: PropTypes.func.isRequired,
  originRef: PropTypes.object,
  destinationRef: PropTypes.object
};

export default ShippingFlowLegs;